import { type TypeTableColumn } from '@thefarmersfront/kitchen';

import { BadgeCell, TextButtonCell } from '~/components/common';
import { ProductSupplierRenderer } from '~/components/product';

import { formatNumber } from '~/utils/common';
import { formatDate } from '~/utils/date';

import { PRODUCT_STATUS_BADGE_COLOR } from '~/constants';

import type { TypeProduct } from '~/types/product.types';

const formatDays = (value: unknown): string => (typeof value === 'number' ? formatNumber(value) : '-');

/**
 * [상품 등록/수정]·[운영 상품 조회] 목록이 함께 쓰는 No 칸 뒤의 컬럼.
 */
export const getProductListColumns = ({
  onClickViewDetail,
}: {
  onClickViewDetail: (skuCode: string) => void;
}): TypeTableColumn<TypeProduct>[] => [
  {
    accessorKey: 'statusName',
    header: '상품 등록 상태',
    size: 120,
    minSize: 100,
    cell: ({ getValue }) => {
      const value = getValue<TypeProduct['statusName']>() ?? '';
      const colorMap = {
        [value]: {
          label: value.replace(/\s/g, ''),
          color: PRODUCT_STATUS_BADGE_COLOR[value as keyof typeof PRODUCT_STATUS_BADGE_COLOR],
        },
      };
      return <BadgeCell value={value} colorMap={colorMap} variant="light" status />;
    },
  },
  {
    accessorKey: 'useMethod',
    header: '상품 사용여부',
    size: 120,
  },
  {
    accessorKey: 'supplierList',
    header: '공급사명',
    size: 196,
    minSize: 100,
    cell: ({ getValue }) => <ProductSupplierRenderer value={getValue<TypeProduct['supplierList']>()} />,
  },
  {
    accessorKey: 'skuName',
    header: '상품명',
    minSize: 240,
    meta: { flex: true },
  },
  {
    accessorKey: 'skuCode',
    header: '상품코드',
    size: 140,
  },
  {
    id: 'viewDetail',
    header: '',
    size: 80,
    minSize: 80,
    cell: ({ row }) => (
      <TextButtonCell showDashIfNoValue={false} onClick={() => onClickViewDetail(row.original.skuCode)}>
        상세보기
      </TextButtonCell>
    ),
  },
  {
    accessorKey: 'temperatureType',
    header: '보관 유형',
    size: 100,
  },
  {
    accessorKey: 'mainCategory',
    header: '대분류',
    size: 140,
  },
  {
    accessorKey: 'middleCategory',
    header: '중분류',
    size: 140,
  },
  {
    accessorKey: 'thirdCategory',
    header: '소분류',
    size: 140,
  },
  {
    accessorKey: 'fourthCategory',
    header: '세분류',
    size: 140,
  },
  {
    accessorKey: 'shelfLifeType',
    header: '기한정책',
    size: 80,
  },
  {
    accessorKey: 'availableDays',
    header: '유통/소비기한일수',
    size: 130,
    cell: ({ getValue }) => formatDays(getValue()),
    meta: { cellStyle: { textAlign: 'right' } },
  },
  {
    accessorKey: 'sellAvailableDays',
    header: '판매가능일수',
    size: 100,
    cell: ({ getValue }) => formatDays(getValue()),
    meta: { cellStyle: { textAlign: 'right' } },
  },
  {
    accessorKey: 'sellDeadlineDays',
    header: '판매마감일수',
    size: 100,
    cell: ({ getValue }) => formatDays(getValue()),
    meta: { cellStyle: { textAlign: 'right' } },
  },
  {
    accessorKey: 'createdDate',
    header: '상품 등록일',
    minSize: 98,
    cell: ({ getValue }) => formatDate(getValue<TypeProduct['createdDate']>(), 'YYYY.MM.DD') ?? '-',
  },
];
